import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Marker,
  Tooltip,
  Polyline,
  useMap,
  useMapEvent,
  Pane,
} from "react-leaflet";
import { MAP_TILE_CONFIG, fromTimestamp, getEnumName, toTimestamp } from "@racoon/lib";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import "leaflet-gesture-handling";
import type { LatLngExpression } from "leaflet";
import {
  makeIcon,
  makeTruckIconDynamic,
  pickupPalette,
  pinBadgeLabel,
  makeTruckChevronIcon,
  pinTooltipOffset,
  fanOffsetPx,
  siteCardPinHtml,
} from "../model/constants/icons";
import { jobStatusTextClass } from "../model/constants/job-status-colors";
import { locationTypeStyle } from "../model/constants/location-types";
import { photoPointsUpTo, type PhotoPoint } from "../model/helpers/photo-points";
import { spreadNudges } from "../model/helpers/spread-locations";
import type { StepJob } from "../model/helpers/step-pins";
import { createPlaybackClock, type PlaybackClock, usePlaybackTime } from "../model/playback-clock";
import { InfoType, useInfoPanel } from "~/features/info-panel/model/useInfoPanel";

import { Minimize2 } from "lucide-react";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { GpsStopsLayer, GpsStopsLegend, useGpsStopsLayer } from "~/features/gps-stops";
import type { PlannedSite } from "~/features/gps-stops/model/useGpsStopsLayer";
import { useFullscreen } from "~/shared/hooks/useFullscreen";
import { MAP_CONTROL_CLASS } from "~/shared/ui/map-control";
import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TimelinePlayer,
  createRouteTimelineSegments,
  findPositionAtTime,
  routeHasPlayableTrack,
  travelPathToTimelinePoints,
  type TimelinePoint,
  type TravelPathLike,
} from "@racoon/ui";
import { BrandIcMapFilter, IcExpandMap } from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { AutoFitBounds } from "../components/AutoFitBounds";
import { StepPickupLayer } from "../components/StepPickupLayer";
import { FlyToPoint, type FocusPoint } from "../components/FlyToPoint";
import { PhotoPointsLayer, PhotoPointsToggle } from "../components/PhotoPointsLayer";
import { useZoomLevel } from "../model/pickup-geofence";
import { realOrgId } from "../model/owner-org";
import { AutoZoomToSelectedObject } from "../components/AutoZoomToSelectedObject";
import { AutoZoomToSingleObject } from "../components/AutoZoomToSingleObject";
import { ZoomTracker } from "../components/ZoomTracker";
import { useCalculateRouteWaypointsMutation } from "~/shared/api/cost-provider/cost-provider.api";
import {
  useLazyGetTruckTravelPathQuery,
  type TravelPathResponse,
} from "~/shared/api/truck/truck.grpc";
import {
  buildRouteVisualization,
  type RouteVisualization,
} from "../model/helpers/route-visualization-builder";
import {
  getLatestTravelPathPoint,
  getTravelPathWindow,
  PLANNED_ROUTE_LOOKBACK_MS,
  type TravelPathPoint,
} from "../model/helpers/travel-path";
import { RouteSegmentsLayer, getZoomCategory } from "../components/RouteSegmentsLayer";
import {
  useGetRouteQuery,
  useLazyListActiveTrucksWithRoutesQuery,
} from "~/shared/api/routes/route.grpc";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { WholeStepWheelZoom } from "../components/whole-step-wheel-zoom";

// Sites this close are one place in practice (a depot and its transfer station).
const SPREAD_WITHIN_M = 500;

const LocationMarkers = ({
  depots,
  selectedId,
  onMarkerClick,
}: {
  depots: MapObject[];
  selectedId?: string | number | null;
  onMarkerClick?: (item: MapObject) => void;
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useMapEvent("zoomend", () => setZoom(map.getZoom()));

  const spread = useMemo(() => {
    const nudges = spreadNudges(
      depots.map((o) => map.project([o.lat, o.lon], zoom)),
      {
        near: (i, j) =>
          map.distance([depots[i].lat, depots[i].lon], [depots[j].lat, depots[j].lon]) <
          SPREAD_WITHIN_M,
      },
    );
    return depots.map((o, i) => (nudges[i] ? { ...o, nudge: nudges[i] } : o));
  }, [depots, map, zoom]);

  return (
    <>
      {spread.map((o, idx) => (
        <Marker
          // Remounts when its spot changes: swapping the icon under the cursor
          // never fires mouseout, so the hover card would stay open.
          key={`depot-${o.id}-${idx}-${o.nudge ?? ""}`}
          position={[o.lat, o.lon]}
          icon={makeIcon(o, String(o.id) === String(selectedId))}
          eventHandlers={{
            click: () => onMarkerClick?.(o),
            mouseover: (e) => e.target.setZIndexOffset(100), // Keep marker above neighbors only
            mouseout: (e) => e.target.setZIndexOffset(0),
          }}
        >
          <MapObjectTooltip item={o} />
        </Marker>
      ))}
    </>
  );
};

const MapResizeWatcher = () => {
  const map = useMap();

  React.useEffect(() => {
    const container = map.getContainer();
    let frame = 0;

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      // pan: false keeps the content where it is on screen (anchored top-left) instead of
      // re-centring, so a neighbour growing or shrinking the map doesn't slide pins and tiles.
      frame = requestAnimationFrame(() => map.invalidateSize({ pan: false }));
    });

    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

const MapUpdater = ({ center, zoom }: { center?: LatLngExpression; zoom?: number }) => {
  const map = useMap();
  const prevCenter = React.useRef<string>("");
  const prevZoom = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (center && zoom) {
      const centerStr = JSON.stringify(center);
      if (centerStr !== prevCenter.current || zoom !== prevZoom.current) {
        map.setView(center, zoom, { animate: true });
        prevCenter.current = centerStr;
        prevZoom.current = zoom;
      }
    }
  }, [center, zoom, map]);
  return null;
};

const floorMinute = (d: Date) => new Date(Math.floor(d.getTime() / 60_000) * 60_000);

export type MapObject = {
  name: string;
  type: InfoType;
  id: number | string;
  lat: number;
  lon: number;
  angle?: number;
  route?: ConnectProto.Route;
  companyName?: string;
  localIndex?: number;
  /** Printed on the pin instead of localIndex. */
  pinLabel?: string;
  /** The job a step pin stands for. */
  jobId?: bigint;
  /** Colours the pin by this job instead of by its site's latest job. */
  jobStatus?: ConnectProto.JobStatus;
  /** Position among pins sharing one site; they fan out around it. */
  fan?: { index: number; count: number };
  /** A collapsed pin: every step it stands for, and how to fan it back out. */
  steps?: string[];
  /** A collapsed pin: the jobs of those steps, lowest step first. */
  stepJobs?: StepJob[];
  onExpand?: () => void;
  /** Set on a collapsed pin whose steps serve several customers. */
  customerCount?: number;
  /** Only this site's geofence, drawn under a fan of step pins. */
  shapeOnly?: boolean;
  /** Step pins: the site they belong to, which their folding is keyed on. */
  siteKey?: string;
  status?: string | ConnectProto.DriverOnShiftStatus;
  containerGroups?: ConnectProto.ContainerGroup[];
  hasOrders?: boolean;
  hasUnassignedOrders?: boolean;
  truckType?: string;
  hasShift?: boolean;
  /** Trucks only: the parent-tier org the travel-path and stops RPCs match on. */
  ownerOrganizationId?: string | number | bigint;
  /** GeoJSON from PickupLocation.geometry; drawn instead of the pin when legible. */
  geometry?: unknown;
  /** Depots only: which kind of location, for its icon and hover card. */
  locationType?: ConnectProto.LocationType;
  /** Depots only: screen offset that keeps it clear of a nearby location. */
  nudge?: [number, number];
};

type Props = {
  center?: LatLngExpression;
  zoom?: number;
  className?: string;
  objects?: MapObject[];
  /** Cluster the pickup pins. Leave off where the stop number matters. */
  clusterPickups?: boolean;
  /** Scrub the drawn route's GPS track. Off where there is no route to play. */
  showPlayer?: boolean;
  /** Whether the player is on screen: it needs a loaded track, not just a playable route. */
  onPlayerVisibleChange?: (visible: boolean) => void;
  /** The host's panel is collapsed to a strip: the player is hidden, not unmounted. */
  collapsed?: boolean;
  /** Print the sequence number on pickup pins. */
  showPickupIndex?: boolean;
  trucks?: MapObject[];
  depots?: MapObject[];
  onMarkerClick?: (item: MapObject) => void;
  forceRouteId?: number;
  previewRoute?: any;
  hideFullscreenButton?: boolean;
  /** Position classes of the control stack (default "top-2 right-2"). A host that
   *  overlays its own control in the first slot passes e.g. "top-12 right-2" so the
   *  map's own controls continue below it. `cn` is a plain join, so the default is
   *  replaced rather than merged. */
  controlStackClassName?: string;
  /** Flies here whenever its key changes, e.g. on picking a job in a table. */
  focusPoint?: FocusPoint | null;
  /** A new value fits the whole map content again, e.g. on unpicking that job. */
  refitKey?: string | number;
  /** A route picked in the info panel is shown alone: other trucks hide, and closing it
   *  returns to the default view. The host hides its own pins. */
  focusRoute?: boolean;
  /** Points the default view also takes in, e.g. the host's own job pins. */
  fitPoints?: { lat: number; lon: number }[];
  /** Extra layers rendered inside the map, e.g. the dashboard's job markers. */
  children?: React.ReactNode;
};

// Pinned to tooltipPane: inside the `pickups` pane a marker's computed z-index
// beats a tooltip's `auto`, so the pin would cover its own label.
const customersKey = (count: number, lang: string) => {
  const form = new Intl.PluralRules(lang).select(count);
  return form === "one"
    ? "stepCustomersOne"
    : form === "few"
      ? "stepCustomersFew"
      : "stepCustomersMany";
};

const CARD_ARROW = "rgba(0, 0, 0, 0.75)";

// A location's card opens around its marker: the card's icon lands on the marker's
// (28px, top of the 32px box anchored bottom-centre). x also cancels Leaflet's 6px margin.
const LOCATION_ICON_PX = 28;
const locationCardOffset = ([dx, dy] = [0, 0]): [number, number] => [
  dx - (8 + LOCATION_ICON_PX / 2 + 6),
  dy - 18,
];

const MAX_CARD_JOBS = 3;

const JobStatusLine = ({
  step,
  jobId,
  status,
}: {
  step?: string;
  jobId?: bigint;
  status?: ConnectProto.JobStatus;
}) => {
  const t = useT();
  const toneText = status != null ? jobStatusTextClass(status) : undefined;
  const statusName = getEnumName(ConnectProto.JobStatusSchema, status);
  return (
    <div
      className={cn("text-xs font-medium leading-4", toneText)}
      style={toneText ? undefined : { color: "#2563EB" }}
    >
      {step && <span className="mr-1.5 text-white/60">#{step}</span>}
      {jobId != null && `Z${String(jobId)}`}
      {statusName && (
        <>
          <span className="mx-1.5 opacity-60">|</span>
          {t(Namespace.Enum, statusName)}
        </>
      )}
    </div>
  );
};

/** Figma «Location Hover Tooltip»: a dark card with the kind's icon, name and label. */
export const MapObjectTooltip = ({ item }: { item: MapObject }) => {
  const t = useT();
  const { lang } = useLang();
  const arrowDx = fanOffsetPx(item);
  const fanned = item.fan != null && item.fan.count > 1;
  const isLocation = item.type.toString() === "depot";
  const location = isLocation ? locationTypeStyle(item.locationType) : undefined;
  const secondary = "text-xs leading-4 text-white/60";
  return (
    // The card of a fan stays centred over the fan; only its arrow follows the
    // hovered pin, so Leaflet's own centred arrow is hidden.
    <Tooltip
      pane="tooltipPane"
      direction={location ? "right" : "top"}
      offset={location ? locationCardOffset(item.nudge) : pinTooltipOffset(item)}
      className={cn(
        "!rounded-lg !border-0 !bg-black/75 !p-2 !text-white !shadow-none",
        "before:!border-t-black/75",
        (fanned || location) && "before:!hidden",
        // Opens out of the marker: starts as just the icon (padding + icon + padding).
        location && "animate-unroll-right [--unroll-from:44px] motion-reduce:animate-none",
      )}
    >
      <div className="flex items-center gap-2">
        {location ? (
          <location.Icon
            width={LOCATION_ICON_PX}
            height={LOCATION_ICON_PX}
            className="shrink-0"
            style={{ color: location.color }}
          />
        ) : (
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center"
            dangerouslySetInnerHTML={{
              __html: siteCardPinHtml(pickupPalette(item, false, item.jobStatus)),
            }}
          />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium leading-5">
            {item.name || `${t(Namespace.Routes, "point")} ${item.localIndex ?? ""}`.trim()}
          </div>
          {location?.labelKey && (
            <div
              className="text-xs font-medium uppercase leading-4"
              style={{ color: location.color }}
            >
              {t(Namespace.GpsTracking, location.labelKey)}
            </div>
          )}
          {/* A shared site's pin already prints its steps; the card names their jobs. */}
          {item.stepJobs && item.stepJobs.length > 1 ? (
            <>
              {item.stepJobs.slice(0, MAX_CARD_JOBS).map((job, i) => (
                <JobStatusLine key={i} step={job.step} jobId={job.jobId} status={job.jobStatus} />
              ))}
              {item.stepJobs.length > MAX_CARD_JOBS && (
                <div className={secondary}>
                  {t(Namespace.Infopanel, "siteJobs.more")} {item.stepJobs.length - MAX_CARD_JOBS}
                </div>
              )}
            </>
          ) : (
            item.jobId != null && <JobStatusLine jobId={item.jobId} status={item.jobStatus} />
          )}
          {item.customerCount ? (
            <div className={secondary}>
              {t(Namespace.GpsTracking, customersKey(item.customerCount, lang), {
                count: item.customerCount,
              })}
            </div>
          ) : (
            item.companyName && <div className={secondary}>{item.companyName}</div>
          )}
        </div>
      </div>
      {fanned && (
        <span
          className="pointer-events-none absolute"
          style={{
            left: `calc(50% + ${arrowDx}px)`,
            bottom: -12,
            marginLeft: -6,
            border: "6px solid transparent",
            borderTopColor: CARD_ARROW,
          }}
        />
      )}
    </Tooltip>
  );
};

const PickupPin = React.memo(
  ({
    item,
    isSelected,
    jobStatus,
    showIndex,
    onClick,
  }: {
    item: MapObject;
    isSelected: boolean;
    jobStatus?: ConnectProto.JobStatus;
    showIndex: boolean;
    onClick: (o: MapObject) => void;
  }) => (
    <Marker
      position={[item.lat, item.lon]}
      icon={makeIcon(item, isSelected, false, undefined, jobStatus, showIndex)}
      eventHandlers={{
        click: () => onClick(item),
        mouseover: (e) => e.target.setZIndexOffset(100), // Keep marker above neighbors only
        mouseout: (e) => e.target.setZIndexOffset(0),
      }}
    >
      <MapObjectTooltip item={item} />
    </Marker>
  ),
);
PickupPin.displayName = "PickupPin";

/** Below this the plate chips collide into an unreadable mass, and the sprite's
 *  own detail is too small to read — a heading chevron says more. */
const TRUCK_DETAIL_MIN_ZOOM = 13;

type Playhead = { clock: PlaybackClock; points: TimelinePoint[] };

const TruckMarker = ({
  truck,
  selected,
  detailed,
  pane,
  onTruckClick,
}: {
  truck: MapObject;
  selected: boolean;
  detailed: boolean;
  pane?: string;
  onTruckClick: (o: MapObject) => void;
}) => (
  <Marker
    {...(pane && { pane })}
    position={[truck.lat, truck.lon]}
    icon={
      detailed
        ? // The sprite is drawn side-on, so its heading is a quarter turn behind.
          makeTruckIconDynamic({ ...truck, angle: (truck.angle ?? 0) - 90 }, selected, true)
        : makeTruckChevronIcon(truck, selected)
    }
    eventHandlers={{ click: () => onTruckClick(truck) }}
  />
);

/** The route's truck at the playhead; the only marker that re-renders per frame. */
const PlayheadTruckMarker = ({
  playhead,
  truck,
  ...rest
}: { playhead: Playhead; truck: MapObject } & Omit<
  React.ComponentProps<typeof TruckMarker>,
  "truck"
>) => {
  const time = usePlaybackTime(playhead.clock);
  const at = time == null ? null : findPositionAtTime(playhead.points, time);
  const placed = at
    ? { ...truck, lat: at.lat, lon: at.lon, angle: at.angle ?? truck.angle }
    : truck;
  return <TruckMarker truck={placed} {...rest} />;
};

const TruckMarkers = ({
  trucks,
  selectedId,
  onTruckClick,
  routeTruckId,
  playhead,
}: {
  trucks: MapObject[];
  selectedId: string | number | null;
  onTruckClick: (o: MapObject) => void;
  /** The drawn route's truck: under its sites' pins, which it would cover. */
  routeTruckId?: string | null;
  /** Set once the player is used: the route's truck follows it. */
  playhead?: Playhead;
}) => {
  const zoom = useZoomLevel();
  // The band, not the number: zooming inside it must not rebuild the fleet.
  const zoomedIn = zoom >= TRUCK_DETAIL_MIN_ZOOM;

  return (
    <>
      {trucks.map((o, idx) => {
        const selected = String(o.id) === String(selectedId);
        const isRouteTruck = routeTruckId != null && String(o.id) === routeTruckId;
        // The chevron exists to keep a crowded city legible; a lone truck is
        // the subject of the map.
        const detailed = selected || zoomedIn || trucks.length === 1;
        const pane = isRouteTruck ? "route-truck" : detailed ? undefined : "truck-chevrons";
        const props = {
          truck: o,
          selected,
          detailed,
          pane,
          onTruckClick,
        };
        // A marker's pane is fixed at creation.
        const key = `truck-${o.id}-${idx}-${pane}`;
        return isRouteTruck && playhead ? (
          <PlayheadTruckMarker key={key} playhead={playhead} {...props} />
        ) : (
          <TruckMarker key={key} {...props} />
        );
      })}
    </>
  );
};

/** The travelled part of the track, up to the playhead. */
const PlaybackTrail = ({ clock, points }: Playhead) => {
  const time = usePlaybackTime(clock);
  const travelled = useMemo(() => {
    if (time == null) return [];
    const path = points
      .filter((pt) => pt.timestamp <= time)
      .map((pt) => [pt.lat, pt.lon] as [number, number]);
    // Up to the truck, which sits between samples, not at the last one.
    const at = findPositionAtTime(points, time);
    if (at) path.push([at.lat, at.lon]);
    return path;
  }, [points, time]);
  if (travelled.length < 2) return null;
  return (
    <Polyline positions={travelled} pathOptions={{ color: "#007AFF", weight: 5, opacity: 0.9 }} />
  );
};

const IDLE_CLOCK = createPlaybackClock();

/** Photos follow the playhead once the player is used; until then all show. */
const PlaybackPhotos = ({ clock, points }: { clock?: PlaybackClock; points?: PhotoPoint[] }) => {
  const time = usePlaybackTime(clock ?? IDLE_CLOCK);
  const shown = useMemo(
    () => (points && time != null ? photoPointsUpTo(points, time) : points),
    [points, time],
  );
  return <PhotoPointsLayer points={shown} />;
};

/** An unchanged track keeps its array, so a route rebuild does not reset the player. */
const useStableTrack = (points: TimelinePoint[], routeId: string | null) => {
  const prev = useRef<{ key: string; points: TimelinePoint[] } | null>(null);
  const key = points.length
    ? `${routeId}|${points.length}|${points[0].timestamp}|${points[points.length - 1].timestamp}`
    : "";
  if (prev.current?.key !== key) prev.current = { key, points };
  return prev.current.points;
};

export const MapWithGeofence = ({
  center = [43.238949, 76.889709],
  zoom = 12,
  className = "h-full w-full z-0",
  clusterPickups = false,
  showPlayer = false,
  onPlayerVisibleChange,
  collapsed = false,
  showPickupIndex = true,
  trucks,
  depots,
  objects,
  onMarkerClick,
  forceRouteId,
  previewRoute,
  hideFullscreenButton = false,
  controlStackClassName = "top-2 right-2",
  focusPoint,
  refitKey,
  focusRoute = false,
  fitPoints,
  children,
}: Props) => {
  const { activeCompanyId } = useAuthSession();
  const [listActiveTrucks, { data: activeTrucksData }] = useLazyListActiveTrucksWithRoutesQuery();
  const statusList = activeTrucksData?.trucksWithRoutes;
  const { infoId, infoType } = useInfoPanel();
  const { isFullscreen, toggleFullscreen, elementRef } = useFullscreen();

  const effectiveInfoId = forceRouteId ? String(forceRouteId) : infoId;
  const effectiveInfoType = forceRouteId ? "route" : infoType;

  // Route visualization state
  const [routeVisualization, setRouteVisualization] = useState<RouteVisualization | null>(null);
  const [playbackTrack, setPlaybackTrack] = useState<{
    path?: TravelPathResponse;
    route?: ConnectProto.Route;
  } | null>(null);
  // The route the player was last used on; until then the map shows the route
  // at rest (no trail, the truck where its page put it, every photo).
  const [engagedRouteId, setEngagedRouteId] = useState<string | null>(null);
  const visualizedRouteIdRef = useRef<string | null>(null);
  const visualizedTruckIdRef = useRef<string | null>(null);
  const [showPhotos, setShowPhotos] = useState(true);
  // Truck + window of the route currently drawn. Derived without awaiting the
  // track, so for a running route its end instant is near the track's but not
  // identical to it.
  const [gpsStopsScope, setGpsStopsScope] = useState<{
    truckId: string;
    ownerOrganizationId: string;
    from: Date;
    to: Date;
    openStart: boolean;
    openEnded: boolean;
    /** The route's sites: a short stop on one of them is a visit, not off-site noise. */
    plannedSites: PlannedSite[];
  } | null>(null);
  // The window is pinned to the minute so the poll stops churning cache keys,
  // which also froze it: a running route would never show a stop recorded after
  // it was selected. Advanced once a minute rather than on every poll.
  useEffect(() => {
    if (!gpsStopsScope?.openEnded) return;
    const id = setInterval(() => {
      setGpsStopsScope((prev) => {
        if (!prev?.openEnded) return prev;
        const to = floorMinute(new Date());
        if (to.getTime() <= prev.to.getTime()) return prev;
        // An open start is a lookback from now, so it slides with `to`; holding
        // it would widen the window without bound for the life of the route.
        const from = prev.openStart
          ? floorMinute(new Date(Date.now() - PLANNED_ROUTE_LOOKBACK_MS))
          : prev.from;
        return { ...prev, from, to };
      });
    }, 60_000);
    return () => clearInterval(id);
  }, [gpsStopsScope?.openEnded]);

  const t = useT();

  const playbackRouteId = playbackTrack?.route?.id != null ? String(playbackTrack.route.id) : null;
  const playbackPoints = useStableTrack(
    useMemo(
      () =>
        showPlayer && routeHasPlayableTrack(playbackTrack?.route)
          ? travelPathToTimelinePoints(
              playbackTrack?.path as TravelPathLike | undefined,
              playbackTrack?.route ? [playbackTrack.route] : [],
            )
          : [],
      [showPlayer, playbackTrack],
    ),
    playbackRouteId,
  );
  // One clock per route: a stale time must not carry over to the next one.
  const clockRef = useRef<{ routeId: string | null; clock: PlaybackClock } | null>(null);
  if (clockRef.current?.routeId !== playbackRouteId) {
    clockRef.current = { routeId: playbackRouteId, clock: createPlaybackClock() };
  }
  const clock = clockRef.current.clock;
  const engaged = engagedRouteId != null && engagedRouteId === playbackRouteId;
  // A grown track (a live route refetched) restarts the player; this keeps its place.
  const playerStartTime = useMemo(() => clock.get(), [clock, playbackPoints]);
  const handlePlaybackTime = React.useCallback(
    (time: number) => {
      clock.set(time);
      if (!engaged) setEngagedRouteId(playbackRouteId);
    },
    [clock, engaged, playbackRouteId],
  );
  const playerVisible = playbackPoints.length > 0;
  useEffect(() => onPlayerVisibleChange?.(playerVisible), [playerVisible, onPlayerVisibleChange]);
  const playhead = useMemo(
    () => (engaged && playbackPoints.length ? { clock, points: playbackPoints } : undefined),
    [engaged, clock, playbackPoints],
  );

  const playbackSegments = useMemo(
    () =>
      playbackPoints.length && playbackTrack?.route
        ? createRouteTimelineSegments(
            [playbackTrack.route],
            playbackPoints,
            (key, options) => t(Namespace.GpsTracking, key as any, options),
            // A route with GPS times but no fact start has no fact segments. The
            // same test as routeHasPlayableTrack: an empty Timestamp is no start.
            (fromTimestamp(playbackTrack.route.factStartAt)?.getTime() ?? 0) > 0 ? "fact" : "gps",
          )
        : [],
    [playbackPoints, playbackTrack, t],
  );

  const gpsStops = useGpsStopsLayer({
    truckId: gpsStopsScope?.truckId,
    ownerOrganizationId: gpsStopsScope?.ownerOrganizationId,
    from: gpsStopsScope?.from,
    to: gpsStopsScope?.to,
    plannedSites: gpsStopsScope?.plannedSites,
  });

  // Position of the visualized route's truck taken from its GPS travel path.
  // Kept keyed by truck id so it survives a re-render of the `trucks` prop and
  // only ever moves the truck it was resolved for.
  const [travelPathTruckPosition, setTravelPathTruckPosition] = useState<
    | (TravelPathPoint & {
        id: string;
        polled: { lat: number; lon: number; angle?: number };
      })
    | null
  >(null);
  const handlePickupClick = React.useCallback(
    (o: MapObject) => (o.onExpand ? o.onExpand() : onMarkerClick?.(o)),
    [onMarkerClick],
  );
  // The band, not the number: only two thresholds matter downstream, so
  // zooming inside a band must not re-render the whole map.
  const [zoomCategory, setZoomCategory] = useState(() => getZoomCategory(zoom));

  // API hooks for route calculation
  const [calculateRouteWaypoints] = useCalculateRouteWaypointsMutation();
  const [getTravelPath] = useLazyGetTruckTravelPathQuery();

  // The truck's org, not the route's: trucks are parent-tier while routes are
  // mostly child-tier, and the RPCs match the truck's owner exactly. The caller's
  // truck wins: ListActiveTrucks does not always nest one under the route.
  const ownerOrgFor = React.useCallback(
    (route: ConnectProto.Route, truckOwnerOrgId?: bigint | number | string | null) =>
      realOrgId(truckOwnerOrgId) ??
      realOrgId(route.driverOnShift?.truck?.ownerOrganizationId) ??
      realOrgId(route.ownerOrganizationId) ??
      activeCompanyId,
    [activeCompanyId],
  );

  // Derived without awaiting the track: the stops come from Postgres and the
  // track from ClickHouse, so the overlay has to survive the track failing.
  const gpsStopsScopeFor = React.useCallback(
    (
      truckId: bigint | number | string,
      route: ConnectProto.Route,
      truckOwnerOrgId?: bigint | number | string | null,
    ) => {
      const ownerOrgId = ownerOrgFor(route, truckOwnerOrgId);
      if (!ownerOrgId) return null;
      const { from, to, openStart, openEnded } = getTravelPathWindow(route);
      // Only the open sides are floored: they come from `now`, and at
      // millisecond precision every publish mints an RTK Query cache key,
      // refetching the stops and blanking the layer. Recorded bounds stay exact.
      const start = openStart ? floorMinute(from) : from;
      const end = openEnded ? floorMinute(to) : to;
      return {
        truckId: String(truckId),
        ownerOrganizationId: String(ownerOrgId),
        from: start,
        // A start recorded inside the current minute floors below itself; pinning
        // to `start` keeps the key stable until the tick advances it, where the
        // raw `to` would churn for the rest of the minute.
        to: end.getTime() < start.getTime() ? start : end,
        openStart,
        openEnded,
        plannedSites: route.jobs.flatMap(({ pickupLocation: pl }) =>
          pl ? [{ name: pl.name, lat: pl.lat, lon: pl.lon, geometry: pl.geometry }] : [],
        ),
      };
    },
    [ownerOrgFor],
  );

  const loadTravelPath = React.useCallback(
    // The id is kept as bigint/string end-to-end: int64 truck ids routed
    // through Number() could silently lose precision.
    // Fetched for every route status, not just started ones: a planned route
    // still needs the truck's recent track to know where the truck actually is.
    async (
      truckId: bigint | number | string,
      route: ConnectProto.Route,
      truckOwnerOrgId?: bigint | number | string | null,
    ) => {
      const ownerOrgId = ownerOrgFor(route, truckOwnerOrgId);

      if (!ownerOrgId) return undefined;

      const { from, to } = getTravelPathWindow(route);

      const res = await getTravelPath({
        id: BigInt(truckId),
        ownerOrganizationId: BigInt(ownerOrgId),
        fromTo: {
          from: toTimestamp(from),
          to: toTimestamp(to),
        },
      }).unwrap();

      return { path: res?.geojsonFeature as TravelPathResponse | undefined };
    },
    [getTravelPath, ownerOrgFor],
  );

  // Trucks as drawn: the visualized route's truck sits on the last point of its
  // travel path, the others keep whatever position their page supplied.
  const routeFitPaths = useMemo(
    () => routeVisualization?.segments.map((seg) => seg.path).filter((p) => p.length > 0),
    [routeVisualization],
  );

  const feedRoute = useMemo(
    () =>
      effectiveInfoType === "route" && effectiveInfoId
        ? statusList?.find((x) => x.routes?.some((r) => String(r.id) === String(effectiveInfoId)))
        : undefined,
    [effectiveInfoType, effectiveInfoId, statusList],
  );
  // A finished or past route is not in the live feed; the route card has fetched it already.
  const { currentData: fetchedRoute } = useGetRouteQuery(
    focusRoute &&
      statusList &&
      !feedRoute &&
      effectiveInfoType === "route" &&
      effectiveInfoId &&
      activeCompanyId
      ? { id: BigInt(effectiveInfoId), ownerOrganizationId: BigInt(activeCompanyId) }
      : skipToken,
  );
  const selectedRouteEntry = useMemo(() => {
    if (feedRoute) return feedRoute;
    const truck = fetchedRoute?.driverOnShift?.truck;
    return fetchedRoute && truck ? { truck, routes: [fetchedRoute] } : undefined;
  }, [feedRoute, fetchedRoute]);

  const focusedTruckId = useMemo(() => {
    if (!focusRoute || effectiveInfoType !== "route") return null;
    const id = selectedRouteEntry?.truck?.id;
    return id != null ? String(id) : null;
  }, [focusRoute, effectiveInfoType, selectedRouteEntry]);

  const displayedTrucks = useMemo(
    () =>
      trucks
        ?.filter((truck) => !focusedTruckId || String(truck.id) === focusedTruckId)
        .map((truck) => {
          const id = String(truck.id);
          // The playhead, once used, moves the route's truck in TruckMarkers.
          const at =
            travelPathTruckPosition && id === travelPathTruckPosition.id && travelPathTruckPosition;
          if (!at) return truck;
          return { ...truck, lat: at.lat, lon: at.lon, angle: at.angle ?? truck.angle };
        }),
    [trucks, travelPathTruckPosition, focusedTruckId],
  );

  // Moves a truck onto its latest GPS point and records it for the marker, so
  // the route segments and the icon start from the same place.
  const applyTravelPathPosition = React.useCallback(
    (truck: MapObject, travelPathData?: TravelPathResponse): MapObject => {
      const latest = getLatestTravelPathPoint(travelPathData);
      const id = String(truck.id);

      if (!latest) {
        // Drop an override left over from another truck, but keep this truck's
        // own last known point so it doesn't flicker between fetches.
        setTravelPathTruckPosition((prev) => (prev && prev.id !== id ? null : prev));
        return truck;
      }

      setTravelPathTruckPosition({
        id,
        ...latest,
        polled: { lat: truck.lat, lon: truck.lon, angle: truck.angle },
      });
      return {
        ...truck,
        lat: latest.lat,
        lon: latest.lon,
        angle: latest.angle ?? truck.angle,
      };
    },
    [],
  );

  // Guards against out-of-order responses: every visualization request takes a
  // token, and only the newest one may write the marker position / route data.
  const visualizationRequestRef = useRef(0);

  const clearVisualization = React.useCallback(() => {
    visualizedRouteIdRef.current = null;
    visualizedTruckIdRef.current = null;
    setRouteVisualization(null);
    setPlaybackTrack(null);
    setGpsStopsScope(null);
    setTravelPathTruckPosition(null);
  }, []);

  // Blanking mid-reload drops every pin's job status, which swaps its icon and
  // makes Leaflet rebuild the element, stranding any open tooltip. Only a
  // different route needs the old one cleared first.
  const clearVisualizationUnless = React.useCallback(
    (routeId: string | number | null | undefined, truckId: bigint | number | string) => {
      // The assign-route draft has no id, so it can never match — always clear.
      // The truck is part of the identity: a reassigned route keeping the old
      // path would pair it with the new truck's stops.
      if (
        routeId != null &&
        visualizedRouteIdRef.current === String(routeId) &&
        visualizedTruckIdRef.current === String(truckId)
      ) {
        return;
      }
      clearVisualization();
    },
    [clearVisualization],
  );

  useEffect(() => {
    if (effectiveInfoType !== "truck" && effectiveInfoType !== "route") {
      clearVisualization();
    }
  }, [effectiveInfoType, clearVisualization]);

  // Consumed on the first poll that moves this truck at all, heading included:
  // the track's last point only bridges the gap until the fleet feed catches up,
  // and inferring that from coordinate equality would revive it on a return trip.
  useEffect(() => {
    const from = travelPathTruckPosition;
    if (!from) return;
    const polled = trucks?.find((t) => String(t.id) === from.id);
    if (!polled) return;
    const moved =
      polled.lat !== from.polled.lat ||
      polled.lon !== from.polled.lon ||
      polled.angle !== from.polled.angle;
    if (moved) setTravelPathTruckPosition(null);
  }, [trucks, travelPathTruckPosition]);

  // Read, never depended on, so the poll's fresh array cannot restart the load.
  const trucksRef = useRef(trucks);
  useLayoutEffect(() => {
    trucksRef.current = trucks;
  });

  // The dashboard repolls every 10s and hands down fresh arrays of the same
  // trucks, which otherwise wiped the route and the stops overlay mid-view.
  // Compared as sets rather than a key: a digest can collide, and a sorted join
  // rebuilds a string over thousands of pickup locations on every poll.
  const mapContentsRef = useRef<Set<string> | null>(null);
  const mapContentsKey = useMemo(() => {
    const next = new Set<string>();
    for (const o of objects ?? []) next.add(`o${o.id}:${o.ownerOrganizationId ?? ""}`);
    for (const t of trucks ?? []) next.add(`t${t.id}:${t.ownerOrganizationId ?? ""}`);
    const prev = mapContentsRef.current;
    // Memo cache, not committed state: recomputing yields the same set, so a
    // discarded render leaves nothing stale behind. Settled during the first
    // render, so the load effect below never starts against a key it then loses.
    if (prev && prev.size === next.size && [...next].every((k) => prev.has(k))) return prev;
    mapContentsRef.current = next;
    return next;
  }, [objects, trucks]);

  useEffect(() => {
    // Fleet churn alone must not wipe a route the dispatcher is watching: only
    // the drawn truck leaving the fleet invalidates what is on screen.
    const drawn = visualizedTruckIdRef.current;
    if (drawn && trucksRef.current?.some((t) => String(t.id) === drawn)) return;
    clearVisualization();
  }, [mapContentsKey, clearVisualization]);

  const handleTruckClick = async (truck: MapObject) => {
    onMarkerClick?.(truck);
    // Truck click no longer triggers route visualization
    // Route visualization is now triggered by clicking routes in Gantt chart
  };

  const handleRouteClick = async (
    route: {
      truck?: ConnectProto.Truck | null;
      routes?: ConnectProto.Route[];
    },
    targetRouteId: string | number,
  ) => {
    const requestId = ++visualizationRequestRef.current;
    const isStale = () => visualizationRequestRef.current !== requestId;

    const truck = trucks?.find((t) => String(t.id) === String(route.truck?.id));
    if (!truck || !route.truck) {
      clearVisualization();
      return;
    }

    clearVisualizationUnless(targetRouteId, route.truck.id);

    const activeRoute = route.routes?.find((r) => String(r.id) === String(targetRouteId));
    if (!activeRoute) {
      clearVisualization();
      return;
    }

    const truckOwnerOrgId = route.truck.ownerOrganizationId;
    // Claimed before the track request, not after: clearVisualizationUnless reads
    // these, and a reload landing while the build is in flight would otherwise
    // clear what is already drawn. The scope goes out here for the same reason.
    if (!isStale()) {
      visualizedRouteIdRef.current = String(targetRouteId);
      visualizedTruckIdRef.current = String(route.truck.id);
      setGpsStopsScope(gpsStopsScopeFor(route.truck.id, activeRoute, truckOwnerOrgId));
    }

    let travelPathData;
    try {
      travelPathData = (await loadTravelPath(route.truck.id, activeRoute, truckOwnerOrgId))?.path;
    } catch (e) {
      if (!isStale()) console.warn("Failed to fetch travel path:", e);
    }

    if (isStale()) return;

    // Build route visualization
    try {
      const visualization = await buildRouteVisualization(
        activeRoute,
        applyTravelPathPosition(truck, travelPathData),
        calculateRouteWaypoints,
        travelPathData,
      );
      if (!isStale()) {
        setRouteVisualization(visualization);
        setPlaybackTrack({ path: travelPathData, route: activeRoute });
      }
    } catch (error) {
      if (!isStale()) {
        console.error("Failed to build route visualization:", error);
        // Scope kept: the stops describe the truck's window, not the drawn route.
        visualizedRouteIdRef.current = null;
        setRouteVisualization(null);
        setPlaybackTrack(null);
      }
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchAndBuildVisualization = async () => {
      const currentTrucks = trucksRef.current;

      if (previewRoute && currentTrucks && currentTrucks.length > 0) {
        const targetTruckId =
          previewRoute.driverOnShift?.truckId || previewRoute.driverOnShift?.truck?.id;
        const routeTruck =
          currentTrucks.find((t) => String(t.id) === String(targetTruckId)) || currentTrucks[0];
        clearVisualizationUnless(previewRoute.id, routeTruck.id);

        const truckOwnerOrgId = routeTruck.ownerOrganizationId;
        if (!isCancelled) {
          visualizedRouteIdRef.current = previewRoute.id != null ? String(previewRoute.id) : null;
          visualizedTruckIdRef.current = String(routeTruck.id);
          setGpsStopsScope(gpsStopsScopeFor(routeTruck.id, previewRoute, truckOwnerOrgId));
        }

        let travelPathData = undefined;
        try {
          travelPathData = (await loadTravelPath(routeTruck.id, previewRoute, truckOwnerOrgId))
            ?.path;
        } catch (e) {
          if (!isCancelled) console.warn("Failed to fetch travel path for preview:", e);
        }

        if (isCancelled) return;

        try {
          const visualization = await buildRouteVisualization(
            previewRoute,
            applyTravelPathPosition(routeTruck, travelPathData),
            calculateRouteWaypoints,
            travelPathData,
          );

          if (!isCancelled) {
            setRouteVisualization(visualization);
            setPlaybackTrack({ path: travelPathData, route: previewRoute });
          }
        } catch (error) {
          if (!isCancelled) {
            console.error("Preview route build error:", error);
            visualizedRouteIdRef.current = null;
            setRouteVisualization(null);
            setPlaybackTrack(null);
          }
        }
      } else if (effectiveInfoType === "truck" && currentTrucks && effectiveInfoId) {
        const truck = currentTrucks.find((t) => String(t.id) === String(effectiveInfoId));
        if (truck) {
          handleTruckClick(truck);
        }
      } else if (effectiveInfoType === "route" && effectiveInfoId) {
        if (selectedRouteEntry) {
          handleRouteClick(selectedRouteEntry, effectiveInfoId);
        }
      }
    };

    fetchAndBuildVisualization();

    return () => {
      isCancelled = true;
      // Invalidate any in-flight handleRouteClick started by this run.
      visualizationRequestRef.current += 1;
    };
  }, [
    previewRoute,
    mapContentsKey,
    effectiveInfoId,
    effectiveInfoType,
    statusList,
    selectedRouteEntry,
    clearVisualization,
    clearVisualizationUnless,
    calculateRouteWaypoints,
    loadTravelPath,
    gpsStopsScopeFor,
    applyTravelPathPosition,
  ]);

  useEffect(() => {
    if (activeCompanyId) {
      listActiveTrucks({
        ownerOrganizationIds: [BigInt(activeCompanyId)],
      });
    }
  }, [activeCompanyId, listActiveTrucks]);

  return (
    <div
      ref={elementRef}
      className={cn(
        "relative z-0 isolate",
        // Leaflet's top-right controls start under the fullscreen/layers row,
        // or at the 8px gutter when that row is empty.
        hideFullscreenButton && !gpsStopsScope
          ? "[&_.leaflet-top.leaflet-right_.leaflet-control]:!mt-2 [&_.leaflet-top.leaflet-right_.leaflet-control]:!mr-2"
          : "[&_.leaflet-top.leaflet-right]:mt-12",
        // 16.8px otherwise; hosts offset against a known height.
        "[&_.leaflet-control-attribution]:!m-0 [&_.leaflet-control-attribution]:!leading-[17px]",
        playbackPoints.length > 0 &&
          !collapsed &&
          "[&_.leaflet-bottom.leaflet-right]:mb-[51px] [&_.leaflet-bottom.leaflet-right]:mr-[85px]",
        isFullscreen ? "h-screen w-screen" : className,
      )}
    >
      {/* Map controls stack down the corner, one 32px slot each. A host may own the
          first slot (the collapsible panel's toggle) and push this stack down. Collapsed
          to a strip, the panel shows the map's left edge (inert): the stack moves there,
          under the panel's toggle, and stays usable. */}
      <div
        className={cn(
          "absolute z-10 flex flex-col gap-2",
          collapsed ? "pointer-events-auto left-2 top-12" : controlStackClassName,
        )}
      >
        {!hideFullscreenButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            // Chrome copied from the layers asset (IcMapFilter) so the two slots read alike.
            className={cn("max-h-8 max-w-8 hover:bg-slate-100", MAP_CONTROL_CLASS)}
          >
            {/* The Figma glyph carries its own padding inside a 32px frame, so it is
                rendered at 32px; the lucide counterpart is sized to match optically. */}
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4 text-[#151A24]" />
            ) : (
              <IcExpandMap className="h-8 w-8 text-[#151A24]" />
            )}
          </Button>
        )}

        {gpsStopsScope && (
          <Popover>
            <PopoverTrigger asChild>
              {/* The 36px art draws its own 32px box at (2,1), the rest is shadow. The
                  button is that box, so it lines up with the 32px slots above it. */}
              <button
                type="button"
                title="Слои карты"
                aria-label="Слои карты"
                className="relative block h-8 w-8"
              >
                <BrandIcMapFilter className="absolute -left-0.5 -top-px h-9 w-9 max-w-none" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-max min-w-72 max-w-[24rem] p-4 z-[1001]">
              <GpsStopsLegend {...gpsStops.legendProps} idPrefix="map-view-gps-stops" />
              {!!routeVisualization?.photoPoints.length && (
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <PhotoPointsToggle
                    enabled={showPhotos}
                    onEnabledChange={setShowPhotos}
                    points={routeVisualization.photoPoints}
                    idPrefix="map-view-photos"
                  />
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>

      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        className={cn(isFullscreen ? "h-full w-full" : className, "relative z-0")}
        doubleClickZoom
        markerZoomAnimation
        wheelDebounceTime={40}
        wheelPxPerZoomLevel={80}
        // Leaflet floors fitBounds to a whole zoom by default, which can leave the
        // fitted content filling barely half the frame. 0.25, not 0, so wheel zooms
        // still land near native tiles instead of permanently scaled ones.
        zoomSnap={0.25}
        zoomControl={false}
      >
        <WholeStepWheelZoom />
        <MapResizeWatcher />
        <MapUpdater center={center} zoom={zoom} />
        <ZoomTracker setCurrentZoom={(z) => setZoomCategory(getZoomCategory(z))} />
        <AutoZoomToSingleObject objects={objects} />

        <AutoFitBounds
          objects={objects}
          trucks={displayedTrucks}
          // A drawn route already reaches its end point if it is still to be
          // driven; a finished one may never have gone back there.
          depots={routeFitPaths?.length ? undefined : depots}
          // Closing the panel returns to the default view, not a fit to the route it leaves.
          routePaths={focusRoute && !effectiveInfoId ? undefined : routeFitPaths}
          refitOnDeselect={focusRoute}
          fitPoints={fitPoints}
          infoId={effectiveInfoId}
          bottomInset={playbackPoints.length > 0 ? 64 : 0}
          refitKey={refitKey}
        />

        {/* A drawn route is fitted as a whole, so zooming to one of its pins would
            undo that. */}
        {!routeFitPaths?.length && (
          <AutoZoomToSelectedObject
            objects={objects}
            trucks={displayedTrucks}
            infoId={effectiveInfoId}
            infoType={effectiveInfoType}
          />
        )}
        <FlyToPoint point={focusPoint} />

        <TileLayer attribution={MAP_TILE_CONFIG.attributionShort} url={MAP_TILE_CONFIG.url} />

        {/* CLUSTERED MARKERS */}
        {/* Own pane: markerPane orders by latitude, so pickups and trucks would
            otherwise cover each other at random. */}
        <Pane name="pickups" style={{ zIndex: 450 }}>
          <StepPickupLayer
            items={objects ?? []}
            cluster={clusterPickups}
            colorFor={(o) =>
              pickupPalette(
                o,
                String(o.id) === String(effectiveInfoId),
                o.jobStatus ?? routeVisualization?.locationJobStatuses.get(Number(o.id)),
              )
            }
            labelFor={showPickupIndex ? pinBadgeLabel : undefined}
            eventHandlersFor={(o) => ({ click: () => onMarkerClick?.(o) })}
            renderMarker={(o) => (
              <PickupPin
                item={o}
                isSelected={String(o.id) === String(effectiveInfoId)}
                jobStatus={o.jobStatus ?? routeVisualization?.locationJobStatuses.get(Number(o.id))}
                showIndex={showPickupIndex}
                onClick={handlePickupClick}
              />
            )}
          />
        </Pane>

        {/* CLUSTERED MARKERS */}
        {/* Above trucks (markerPane, 600), whose plates would otherwise bury a site
            they crowd; below tooltipPane (650) so hover cards still cover it. */}
        <Pane name="locations" style={{ zIndex: 620 }}>
          {depots && (
            <LocationMarkers
              depots={depots}
              selectedId={effectiveInfoId}
              onMarkerClick={onMarkerClick}
            />
          )}
        </Pane>

        {/* Under the pickups (450) on a route's map: the truck plays over its own
            sites and would take their clicks. Fleet maps keep trucks on top. */}
        <Pane name="route-truck" style={{ zIndex: 445 }} />
        {/* Chevrons stay under the site clusters (450), whose counts they would cover. */}
        <Pane name="truck-chevrons" style={{ zIndex: 440 }} />
        <TruckMarkers
          trucks={displayedTrucks ?? []}
          selectedId={effectiveInfoId}
          onTruckClick={handleTruckClick}
          routeTruckId={routeVisualization ? visualizedTruckIdRef.current : null}
          playhead={playhead}
        />

        {/* Route visualization segments with 3D effect */}
        {routeVisualization && (
          <FeatureGroup>
            <RouteSegmentsLayer
              segments={routeVisualization.segments}
              zoomCategory={zoomCategory}
            />
          </FeatureGroup>
        )}
        {/* GPS dwell events for the route currently drawn */}
        <GpsStopsLayer {...gpsStops.layerProps} />

        {playhead && <PlaybackTrail {...playhead} />}
        {showPhotos && (
          <PlaybackPhotos clock={playhead?.clock} points={routeVisualization?.photoPoints} />
        )}
        {children}
      </MapContainer>

      {playbackPoints.length > 0 && (
        <div className={cn("absolute bottom-2 left-2 right-2 z-[1000]", collapsed && "hidden")}>
          <TimelinePlayer
            points={playbackPoints}
            segments={playbackSegments}
            onTimeChange={handlePlaybackTime}
            initialTime={playerStartTime}
          />
        </div>
      )}
    </div>
  );
};
